import React from 'react';
import { PermissionCode, Permissions } from '../../features/admin/hooks/usePermissions';

export type NavGroup = 'WORKSPACE' | 'ADMINISTRATION' | 'SETTINGS';

export type NavIcon = React.ComponentType<{ size?: number; className?: string }>;

export interface NavItemConfig {
  id: string;
  label: string;
  shortLabel?: string;
  path: string;
  group: NavGroup;
  icon: NavIcon;
  requiredPermission?: PermissionCode;
  roles?: ('SUPER_ADMIN' | 'PLATFORM_SUPER_ADMIN' | 'TENANT_ADMIN' | 'TRAINER' | 'LEARNER')[];
  adminOnly?: boolean;
  platformAdminOnly?: boolean;
  badge?: string;
  isBottomBarCandidate?: boolean;
}

export interface BottomBarItem {
  id: string;
  label: string;
  path: string;
  icon: NavIcon;
  isActive: (currentPath: string) => boolean;
}

// ---------------------------------------------------------------------------
// Reusable SVG Icon Renderers (Standard Lucide-style, NO emojis)
// ---------------------------------------------------------------------------

export const HomeIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

export const DashboardIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="7" height="9" x="3" y="3" rx="1" />
    <rect width="7" height="5" x="14" y="3" rx="1" />
    <rect width="7" height="9" x="14" y="12" rx="1" />
    <rect width="7" height="5" x="3" y="16" rx="1" />
  </svg>
);

export const MonitorPlayIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="20" height="14" x="2" y="3" rx="2" />
    <line x1="8" x2="16" y1="21" y2="21" />
    <line x1="12" x2="12" y1="17" y2="21" />
    <polygon points="10 8 15 10 10 12 10 8" />
  </svg>
);

export const ClipboardCheckIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="m9 14 2 2 4-4" />
  </svg>
);

export const ChartReportIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 16v5" />
    <path d="M16 14v7" />
    <path d="M20 10v11" />
    <path d="m22 3-8.646 8.646a.5.5 0 0 1-.708 0L9.354 8.354a.5.5 0 0 0-.707 0L2 15" />
    <path d="M4 18v3" />
    <path d="M8 14v7" />
  </svg>
);

export const UsersRoundIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 21a8 8 0 0 0-16 0" />
    <circle cx="10" cy="8" r="5" />
    <path d="M22 20c0-3.37-2-6.5-5-8a5 5 0 0 0-.45-8.3" />
  </svg>
);

export const Building2Icon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
    <path d="M10 6h4" />
    <path d="M10 10h4" />
    <path d="M10 14h4" />
    <path d="M10 18h4" />
  </svg>
);

export const UserRoundIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="8" r="5" />
    <path d="M20 21a8 8 0 0 0-16 0" />
  </svg>
);

export const SettingsIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const SlidersIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="4" x2="4" y1="21" y2="14" />
    <line x1="4" x2="4" y1="10" y2="3" />
    <line x1="12" x2="12" y1="21" y2="12" />
    <line x1="12" x2="12" y1="8" y2="3" />
    <line x1="20" x2="20" y1="21" y2="16" />
    <line x1="20" x2="20" y1="12" y2="3" />
    <line x1="1" x2="7" y1="14" y2="14" />
    <line x1="9" x2="15" y1="8" y2="8" />
    <line x1="17" x2="23" y1="16" y2="16" />
  </svg>
);

export const CpuIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="16" height="16" x="4" y="4" rx="2" />
    <rect width="6" height="6" x="9" y="9" rx="1" />
    <path d="M15 2v2" />
    <path d="M15 20v2" />
    <path d="M2 15h2" />
    <path d="M2 9h2" />
    <path d="M20 15h2" />
    <path d="M20 9h2" />
    <path d="M9 2v2" />
    <path d="M9 20v2" />
  </svg>
);

export const CloudIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
  </svg>
);

export const ShieldCheckIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export const FileCheckIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="m9 15 2 2 4-4" />
  </svg>
);

export const BarChart2Icon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="18" x2="18" y1="20" y2="10" />
    <line x1="12" x2="12" y1="20" y2="4" />
    <line x1="6" x2="6" y1="20" y2="14" />
  </svg>
);

// ---------------------------------------------------------------------------
// Master Navigation Registry
// ---------------------------------------------------------------------------

export const ALL_NAV_ITEMS: NavItemConfig[] = [
  // ── WORKSPACE GROUP ──────────────────────────────────────────────────
  {
    id: 'dashboard',
    label: 'Learner Portal',
    shortLabel: 'Home',
    path: '/dashboard',
    group: 'WORKSPACE',
    icon: HomeIcon,
  },
  {
    id: 'simulations_learner',
    label: 'Simulations',
    shortLabel: 'Simulations',
    path: '/dashboard',
    group: 'WORKSPACE',
    icon: MonitorPlayIcon,
  },
  {
    id: 'attempts',
    label: 'My Attempts & Results',
    shortLabel: 'My Results',
    path: '/attempts',
    group: 'WORKSPACE',
    icon: ClipboardCheckIcon,
  },
  {
    id: 'scenarios_studio',
    label: 'Scenarios Studio',
    shortLabel: 'Studio',
    path: '/scenarios',
    group: 'WORKSPACE',
    icon: MonitorPlayIcon,
    requiredPermission: Permissions.SCENARIO_READ,
    adminOnly: true,
  },
  {
    id: 'learners_admin',
    label: 'Learners & Users',
    shortLabel: 'Learners',
    path: '/admin/users',
    group: 'WORKSPACE',
    icon: UsersRoundIcon,
    adminOnly: true,
  },
  {
    id: 'reports',
    label: 'Reports & Analytics',
    shortLabel: 'Reports',
    path: '/admin/reports',
    group: 'WORKSPACE',
    icon: ChartReportIcon,
    requiredPermission: Permissions.ANALYTICS_EXPORT,
    adminOnly: true,
  },

  // ── ADMINISTRATION GROUP ─────────────────────────────────────────────
  {
    id: 'admin_console',
    label: 'Administration Console',
    shortLabel: 'Admin',
    path: '/admin',
    group: 'ADMINISTRATION',
    icon: DashboardIcon,
    adminOnly: true,
  },
  {
    id: 'tenants_list',
    label: 'Organizations',
    shortLabel: 'Organizations',
    path: '/admin/tenants',
    group: 'ADMINISTRATION',
    icon: Building2Icon,
    platformAdminOnly: true,
  },
  {
    id: 'evaluations_studio',
    label: 'Evaluation Rubrics',
    shortLabel: 'Rubrics',
    path: '/admin/evaluations',
    group: 'ADMINISTRATION',
    icon: FileCheckIcon,
    adminOnly: true,
  },
  {
    id: 'analytics_deep',
    label: 'Analytics Console',
    shortLabel: 'Analytics',
    path: '/admin/analytics',
    group: 'ADMINISTRATION',
    icon: BarChart2Icon,
    adminOnly: true,
  },
  {
    id: 'llm_logs',
    label: 'LLM Audit & Logs',
    shortLabel: 'LLM Logs',
    path: '/admin/llm-logs',
    group: 'ADMINISTRATION',
    icon: CpuIcon,
    adminOnly: true,
  },
  {
    id: 'audit_logs',
    label: 'Security Audit Trail',
    shortLabel: 'Audit',
    path: '/admin/audit-logs',
    group: 'ADMINISTRATION',
    icon: ShieldCheckIcon,
    adminOnly: true,
  },

  // ── SETTINGS GROUP ───────────────────────────────────────────────────
  {
    id: 'org_settings',
    label: 'Organization Settings',
    shortLabel: 'Org Settings',
    path: '/admin/settings',
    group: 'SETTINGS',
    icon: SettingsIcon,
    adminOnly: true,
  },
  {
    id: 'udf_settings',
    label: 'Custom Fields & UDF',
    shortLabel: 'Custom Fields',
    path: '/admin/settings/udf',
    group: 'SETTINGS',
    icon: SlidersIcon,
    adminOnly: true,
  },
  {
    id: 'llm_config',
    label: 'LLM Provider Config',
    shortLabel: 'AI Providers',
    path: '/admin/llm-config',
    group: 'SETTINGS',
    icon: CpuIcon,
    adminOnly: true,
  },
  {
    id: 'storage_config',
    label: 'Media & Storage Config',
    shortLabel: 'Storage Vault',
    path: '/admin/settings/storage',
    group: 'SETTINGS',
    icon: CloudIcon,
    adminOnly: true,
  },
];

// ---------------------------------------------------------------------------
// Role-based Bottom Navigation Builder
// ---------------------------------------------------------------------------

export function getRoleBottomNavItems(options: {
  isPlatformAdmin: boolean;
  isTenantAdmin: boolean;
  can?: (perm: PermissionCode) => boolean;
  learnerCanViewReport: boolean;
  onOpenProfileDrawer?: () => void;
}): BottomBarItem[] {
  const { isPlatformAdmin, isTenantAdmin, learnerCanViewReport } = options;

  // 1. SUPER ADMIN (PLATFORM SUPER ADMIN)
  if (isPlatformAdmin) {
    return [
      {
        id: 'super_admin_home',
        label: 'Home',
        path: '/admin',
        icon: DashboardIcon,
        isActive: (p) => p === '/admin' || p === '/dashboard',
      },
      {
        id: 'super_admin_orgs',
        label: 'Organizations',
        path: '/admin/tenants',
        icon: Building2Icon,
        isActive: (p) => p.startsWith('/admin/tenants'),
      },
      {
        id: 'super_admin_scenarios',
        label: 'Simulations',
        path: '/scenarios',
        icon: MonitorPlayIcon,
        isActive: (p) => p.startsWith('/scenarios'),
      },
      {
        id: 'super_admin_reports',
        label: 'Reports',
        path: '/admin/reports',
        icon: ChartReportIcon,
        isActive: (p) => p.startsWith('/admin/reports') || p.startsWith('/reports'),
      },
      {
        id: 'super_admin_more',
        label: 'Menu',
        path: '#menu',
        icon: UserRoundIcon,
        isActive: () => false,
      },
    ];
  }

  // 2. TENANT ADMIN
  if (isTenantAdmin) {
    return [
      {
        id: 'tenant_admin_home',
        label: 'Home',
        path: '/dashboard',
        icon: DashboardIcon,
        isActive: (p) => p === '/dashboard' || p === '/admin',
      },
      {
        id: 'tenant_admin_simulations',
        label: 'Simulations',
        path: '/scenarios',
        icon: MonitorPlayIcon,
        isActive: (p) => p.startsWith('/scenarios'),
      },
      {
        id: 'tenant_admin_learners',
        label: 'Learners',
        path: '/admin/users',
        icon: UsersRoundIcon,
        isActive: (p) => p.startsWith('/admin/users'),
      },
      {
        id: 'tenant_admin_reports',
        label: 'Reports',
        path: '/admin/reports',
        icon: ChartReportIcon,
        isActive: (p) => p.startsWith('/admin/reports') || p.startsWith('/reports'),
      },
      {
        id: 'tenant_admin_menu',
        label: 'Menu',
        path: '#menu',
        icon: UserRoundIcon,
        isActive: () => false,
      },
    ];
  }

  // 3. LEARNER
  const learnerItems: BottomBarItem[] = [
    {
      id: 'learner_home',
      label: 'Home',
      path: '/dashboard',
      icon: HomeIcon,
      isActive: (p) => p === '/' || p === '/dashboard',
    },
    {
      id: 'learner_simulations',
      label: 'Simulations',
      path: '/dashboard#simulations',
      icon: MonitorPlayIcon,
      isActive: (p) => p === '/dashboard' && window.location.hash === '#simulations',
    },
  ];

  // Only show results/reports if learnerCanViewOwnResultReport is NOT disabled
  if (learnerCanViewReport) {
    learnerItems.push({
      id: 'learner_results',
      label: 'My Results',
      path: '/attempts',
      icon: ClipboardCheckIcon,
      isActive: (p) => p.startsWith('/attempts') || p.startsWith('/results'),
    });
  }

  // Profile / Menu Drawer Item
  learnerItems.push({
    id: 'learner_profile',
    label: 'Profile',
    path: '#profile',
    icon: UserRoundIcon,
    isActive: () => false,
  });

  return learnerItems;
}
